import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Tabs, Tab, Box, Typography, Button, Grid, Paper, Divider, Table, TableHead, TableBody, TableCell, TableContainer, TableRow, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { normalizeExperiment, deriveTotals } from './experimentNormalization';
import { useAuth0 } from '@auth0/auth0-react';

// Use environment variable for API URL
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function ExperimentDetails() {
    const { exptID } = useParams();
    const [experiment, setExperiment] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const { getAccessTokenSilently } = useAuth0();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);

    const handleMenuOpen = (event, roomID) => {
        setAnchorEl(event.currentTarget);
        setSelectedRoom(roomID);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRoom(null);
    };

    useEffect(() => {
        const fetchExperimentDetails = async () => {
            console.log("Fetching experiment details for ID:", exptID);
            const token = await getAccessTokenSilently();
            await axios.get(`${API_URL}/api/experiment/${exptID}/details`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((response) => {
                    console.log(response.data)
                    const raw = response.data.exptData[0];
                    const norm = normalizeExperiment(raw);
                    setExperiment({ ...raw, _normalized: norm });
                    setSessions(response.data.exptSessions);
                })
                .catch((error) => {
                    console.error('Error fetching experiment details:', error);
                });
        }
        fetchExperimentDetails();
    }, [getAccessTokenSilently, exptID]);


    if (!experiment) {
        return <Typography>Loading...</Typography>;
    }

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    // Helper to format session data for downloads
    const getSessionSectionData = (session, section) => {
        switch (section) {
            case 'results': {
                // Prefer the participant-specific materialized snapshot: this
                // is the randomized order actually shown to the participant and
                // used by crew subprocess launches. exptConfig is the authored
                // pre-randomization order.
                const configBlocks = session.materialized?.task?.blocks || session.exptConfig?.task?.blocks || [];
                const data = [];
                Object.entries(session.exptData).forEach(([key, blockData]) => {
                    if (key === 'metadata') return;
                    const blockNo = Number(key);
                    let configBlock = configBlocks.find(b => b.block_number === blockNo + 1);
                    if (!configBlock && Array.isArray(configBlocks)) {
                        configBlock = configBlocks[blockNo];
                    }
                    configBlock = configBlock || {};
                    const blockType = configBlock.type || blockData.blockResponses?.type || '';
                    if (blockType === 'conversation') {
                        const msgs = blockData.messages || [];
                        // Separate conversation messages (Agent/Crew + User) from Explanation messages
                        const convoMsgs = msgs.filter(m => m.type !== 'Explanation');
                        let trialCount = 0;
                        // Include first user message edge case
                        if (convoMsgs[0]?.type === 'User') {
                            trialCount++;
                            data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: '', response: convoMsgs[0].msg, timestamp: convoMsgs[0].timestamp });
                        }
                        // Main conversation pairs
                        for (let i = 0; i < convoMsgs.length - 1; i++) {
                            const a = convoMsgs[i], b = convoMsgs[i + 1];
                            if ((a.type === 'Crew' || a.type === 'Agent') && b.type === 'User') {
                                trialCount++;
                                data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: a.msg, response: b.msg, timestamp: a.timestamp });
                            }
                        }
                        // Include last agent message edge case
                        const lastMsg = convoMsgs[convoMsgs.length - 1];
                        if (lastMsg && (lastMsg.type === 'Crew' || lastMsg.type === 'Agent')) {
                            trialCount++;
                            data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: lastMsg.msg, response: '', timestamp: lastMsg.timestamp });
                        }
                        // Explanation messages as separate rows
                        for (const m of msgs.filter(m => m.type === 'Explanation')) {
                            trialCount++;
                            let explanationData = {};
                            try { explanationData = typeof m.msg === 'string' ? JSON.parse(m.msg) : m.msg; } catch { /* noop */ }
                            data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: 'explanation', response: explanationData.text || explanationData.explanation || m.msg, explanationScore: explanationData.explanationData?.score ?? '', explanationLabel: explanationData.explanationData?.label ?? '', explanationTraits: JSON.stringify(explanationData.explanationData?.nearest_traits || []), timestamp: m.timestamp });
                        }
                    } else if (blockType === 'primed_conversation') {
                        // New custom_experiment block type. Aim to match legacy row structure.
                        const stimuliSetName = configBlock.stimuliSetMeta?.name || configBlock.stimuliSet?.name || 'unknown_set';
                        let trialCount = 0;

                        // Helper: push a conversation row, content is stimulus context only; agent message separated
                        const pushConversationRow = (stimLabel, agentText, userReply, timestamp) => {
                            trialCount++;
                            const content = stimLabel ? `${stimuliSetName}|${stimLabel}` : '';
                            data.push({
                                exptID: session.exptID,
                                userID: session.userID,
                                blockNo,
                                blockType,
                                trial: trialCount,
                                content,
                                agentMessage: agentText || '',
                                response: userReply || '',
                                timestamp: timestamp || blockData.blockFinishedTime || ''
                            });
                        };

                        // Helper: push a question response row with stimulus context
                        const pushQuestionRow = (stimLabel, questionText, responseText, timestamp, questionId) => {
                            trialCount++;
                            const content = `${stimuliSetName}|${stimLabel}|${questionText || ''}`;
                            data.push({
                                exptID: session.exptID,
                                userID: session.userID,
                                blockNo,
                                blockType,
                                trial: trialCount,
                                content,
                                response: responseText || '',
                                questionId: questionId || '',
                                timestamp: timestamp || blockData.blockFinishedTime || ''
                            });
                        };

                        const mode = blockData.blockResponses?.mode || configBlock.conversationMode || 'per_stimulus';
                        const stimuliById = Array.isArray(configBlock.stimuli) ? configBlock.stimuli.reduce((acc, s) => { acc[s.id] = s; return acc; }, {}) : {};

                        // Preferred source for per-stimulus details
                        const stimResponses = blockData.blockResponses?.stimuli_responses;
                        if (Array.isArray(stimResponses) && mode === 'per_stimulus') {
                            for (const sr of stimResponses) {
                                const stimLabel = sr.stimulusLabel || stimuliById[sr.stimulusId]?.label || `stimulus_${(sr.trialIndex ?? 0) + 1}`;

                                // 1) Conversation transcript (per stimulus)
                                const transcript = Array.isArray(sr.conversation_transcript) ? sr.conversation_transcript : [];
                                // Separate conversation messages from Explanation messages
                                const convo = transcript
                                    .filter(m => m && m.text && m.sender && m.sender !== 'SYSTEM' && m.sender !== 'Explanation')
                                    .map(m => ({ type: m.sender, msg: m.text, timestamp: sr.completedAt }));
                                const explanationMsgs = transcript.filter(m => m && m.sender === 'Explanation');

                                if (convo.length > 0) {
                                    // First user-only edge
                                    if (convo[0]?.type === 'User') {
                                        pushConversationRow(stimLabel, '', convo[0].msg, convo[0].timestamp);
                                    }
                                    // Pair Agent/Crew → User
                                    for (let i = 0; i < convo.length - 1; i++) {
                                        const a = convo[i], b = convo[i + 1];
                                        if ((a.type === 'Agent' || a.type === 'Crew') && b.type === 'User') {
                                            pushConversationRow(stimLabel, a.msg, b.msg, a.timestamp);
                                        }
                                    }
                                    // Last agent-only edge
                                    const last = convo[convo.length - 1];
                                    if (last && (last.type === 'Agent' || last.type === 'Crew')) {
                                        pushConversationRow(stimLabel, last.msg, '', last.timestamp);
                                    }
                                }

                                // Explanation messages as separate rows
                                for (const em of explanationMsgs) {
                                    trialCount++;
                                    const content = stimLabel ? `${stimuliSetName}|${stimLabel}|explanation` : 'explanation';
                                    data.push({
                                        exptID: session.exptID, userID: session.userID, blockNo, blockType,
                                        trial: trialCount, content,
                                        response: em.text || '',
                                        explanationScore: em.explanationData?.score ?? '',
                                        explanationLabel: em.explanationData?.label ?? '',
                                        explanationTraits: JSON.stringify(em.explanationData?.nearest_traits || []),
                                        timestamp: sr.completedAt || ''
                                    });
                                }

                                // 2) Question responses (per stimulus)
                                const respMap = sr.responses || {};
                                const stimCfg = stimuliById[sr.stimulusId];
                                const questions = Array.isArray(stimCfg?.questions) ? stimCfg.questions : [];
                                for (const [qId, resp] of Object.entries(respMap)) {
                                    const qText = questions.find(q => q.id === qId)?.text || '';
                                    pushQuestionRow(stimLabel, qText, resp, sr.completedAt, qId);
                                }
                            }
                        } else {
                            // Fallback to block-level messages formatting akin to legacy conversation
                            const msgs = blockData.messages || [];
                            const convoMsgs = msgs.filter(m => m.type !== 'Explanation');
                            // First user-only edge
                            if (convoMsgs[0]?.type === 'User') {
                                pushConversationRow('', '', convoMsgs[0].msg, convoMsgs[0].timestamp);
                            }
                            // Pair Agent/Crew → User
                            for (let i = 0; i < convoMsgs.length - 1; i++) {
                                const a = convoMsgs[i], b = convoMsgs[i + 1];
                                if ((a.type === 'Crew' || a.type === 'Agent') && b.type === 'User') {
                                    pushConversationRow('', a.msg, b.msg, a.timestamp);
                                }
                            }
                            // Last agent-only edge
                            const lastMsg = convoMsgs[convoMsgs.length - 1];
                            if (lastMsg && (lastMsg.type === 'Crew' || lastMsg.type === 'Agent')) {
                                pushConversationRow('', lastMsg.msg, '', lastMsg.timestamp);
                            }
                            // Explanation messages as separate rows
                            for (const m of msgs.filter(m => m.type === 'Explanation')) {
                                trialCount++;
                                let explanationData = {};
                                try { explanationData = typeof m.msg === 'string' ? JSON.parse(m.msg) : m.msg; } catch { /* noop */ }
                                data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: 'explanation', response: explanationData.text || explanationData.explanation || m.msg, explanationScore: explanationData.explanationData?.score ?? '', explanationLabel: explanationData.explanationData?.label ?? '', explanationTraits: JSON.stringify(explanationData.explanationData?.nearest_traits || []), timestamp: m.timestamp });
                            }
                        }
                    } else if (['questionnaire','scale'].includes(blockType)) {
                        const responses = blockData.blockResponses || {};
                        let trialCount = 0;
                        // Build a quick lookup for questions by id if available
                        const questionArr = Array.isArray(configBlock.questions) ? configBlock.questions : [];
                        const byId = questionArr.reduce((acc, q) => { if (q && q.id != null) acc[String(q.id)] = q; return acc; }, {});

                        Object.entries(responses).forEach(([k, resp]) => {
                            trialCount++;
                            let question_text = '';
                            let question_id = '';
                            if (byId[String(k)]) {
                                question_text = byId[String(k)].text || '';
                                question_id = byId[String(k)].id || String(k);
                            } else if (!Number.isNaN(parseInt(k, 10)) && questionArr.length) {
                                const idx = parseInt(k, 10) - 1; // common pattern for scale ids '1'..'N'
                                if (idx >= 0 && idx < questionArr.length) {
                                    question_text = questionArr[idx].text || '';
                                    question_id = questionArr[idx].id != null ? String(questionArr[idx].id) : String(k);
                                }
                            } else {
                                question_id = String(k);
                            }
                            data.push({ exptID: session.exptID, userID: session.userID, blockNo, blockType, trial: trialCount, content: question_text, response: resp ?? '', questionId: question_id, timestamp: blockData.blockFinishedTime || '' });
                        });
                    } else if (blockType === 'stimuli') {
                        // Handle stimuli block responses
                        const stimuliBlockResponses = blockData.blockResponses || {};
                        const stimuliSetName = stimuliBlockResponses.stimuliSetName || configBlock.stimuliSetMeta?.name || configBlock.stimuliSet?.name || 'unknown_set';
                        const responses = stimuliBlockResponses.responses || [];
                        let trialCount = 0;
                        
                        responses.forEach((stimulusResponse, index) => {
                            const stimulusFile = stimulusResponse.stimulusFile || {};
                            const stimulusTitle = stimulusFile.title || stimulusFile.label || stimulusFile.originalName || stimulusFile.filename || `stimulus_${index + 1}`;
                            const questionResponses = stimulusResponse.responses || {};
                            const questions = Array.isArray(stimulusFile.questions) && stimulusFile.questions.length > 0
                                ? stimulusFile.questions
                                : (configBlock.questions || []);
                            
                            // Add entry for viewTime if it exists
                            if (stimulusResponse.viewTime !== undefined) {
                                trialCount++;
                                const content = `${stimuliSetName}|${stimulusTitle}`;
                                data.push({ 
                                    exptID: session.exptID, 
                                    userID: session.userID, 
                                    blockNo, 
                                    blockType, 
                                    trial: trialCount, 
                                    content: content, 
                                    response: stimulusResponse.viewTime, 
                                    timestamp: stimulusResponse.timestamp || '' 
                                });
                            }
                            
                            // Add entries for each question response
                            questions.forEach((question, questionIndex) => {
                                trialCount++;
                                const response = questionResponses[question.id] ?? '';
                                const content = `${stimuliSetName}|${stimulusTitle}|${question.text}`;
                                data.push({ 
                                    exptID: session.exptID, 
                                    userID: session.userID, 
                                    blockNo, 
                                    blockType, 
                                    trial: trialCount, 
                                    content: content, 
                                    response: response, 
                                    questionId: question.id || '',
                                    timestamp: stimulusResponse.timestamp || '' 
                                });
                            });
                        });
                    }
                });
                return data;
            }
            case 'metadata':
                return { metadata: session.exptData.metadata, crewEvents: session.crewEvents };
            case 'exptConfig':
                return session.exptConfig;
            case 'raw':
            default:
                return session;
        }
    };

    // Helper to trigger JSON download
    const downloadJSON = (data, filename) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownload = (roomID, section) => {
        const expIdStr = exptID;
        if (roomID === 'all') {
            // ExptConfig for all is identical, download once
            if (section === 'exptConfig') {
                downloadJSON(sessions[0]?.exptConfig, `${expIdStr}_exptConfig.json`);
                return;
            }
            const allData = {};
            sessions.forEach(session => {
                allData[session.roomID] = getSessionSectionData(session, section);
            });
            downloadJSON(allData, `${expIdStr}_all_${section}.json`);
            return;
        }
        const sessionData = sessions.find(sess => sess.roomID === roomID);
        if (!sessionData) return;
        const dataToDownload = getSessionSectionData(sessionData, section);
        const filename = `session_${roomID}_${section}.json`;
        downloadJSON(dataToDownload, filename);

    };

    const getSessionTiming = (session) => {
        // Extract all timestamps from the session
        const timestamps = [];
        
        // Get timestamps from all blocks
        Object.entries(session.exptData).forEach(([key, blockData]) => {
            if (key === 'metadata') return;
            
            // Add blockFinishedTime if it exists
            if (blockData.blockFinishedTime) {
                timestamps.push(new Date(blockData.blockFinishedTime));
            }
            
            // Add message timestamps if they exist
            if (blockData.messages && Array.isArray(blockData.messages)) {
                blockData.messages.forEach(message => {
                    if (message.timestamp) {
                        timestamps.push(new Date(message.timestamp));
                    }
                });
            }
            
            // Add stimuli response timestamps if they exist
            if (blockData.blockResponses?.responses && Array.isArray(blockData.blockResponses.responses)) {
                blockData.blockResponses.responses.forEach(response => {
                    if (response.timestamp) {
                        timestamps.push(new Date(response.timestamp));
                    }
                });
            }
        });
        
        if (timestamps.length === 0) {
            return { startDate: null, duration: "No timing data" };
        }
        
        // Sort timestamps to get first and last
        timestamps.sort((a, b) => a - b);
        const startTime = timestamps[0];
        const endTime = timestamps[timestamps.length - 1];
        
        // Calculate duration in minutes
        const durationMs = endTime.getTime() - startTime.getTime();
        const durationMinutes = Math.round(durationMs / (1000 * 60));
        
        return {
            startDate: startTime,
            duration: `${durationMinutes} minutes`
        };
    };

    const renderBlockSummary = () => {
        const rawBlocks = Array.isArray(experiment.task.blocks) ? experiment.task.blocks : [];
        const isCustom = experiment.task?.type === 'custom_experiment';
        const normalizedBlocks = experiment._normalized?.blocks || [];

        if (rawBlocks.length === 0) {
            return (
                <Box mt={2}>
                    <Typography variant="h5" gutterBottom>
                        Experiment Structure
                    </Typography>
                    <Typography>No blocks available in this experiment.</Typography>
                </Box>
            );
        }

        return (
            <Box mt={2}>
                <Typography variant="h5" gutterBottom>
                    Experiment Structure
                </Typography>
                <Grid container spacing={2}>
                                        {(isCustom ? normalizedBlocks : rawBlocks).map((block, index) => {
                                                const type = block.type;
                                                const trialCount = isCustom ? block.trialCount : (block.type === 'stimuli' ? block.stimuliSet?.files?.length || 0 : block.num_trials || 0);
                                                const extraLines = [];
                                                if (isCustom) {
                                                    if (type === 'primed_conversation') {
                                                        extraLines.push(`Mode: ${block.conversationMode}`);
                                                        if (typeof block.stimuliCount === 'number') extraLines.push(`Stimuli: ${block.stimuliCount}`);
                                                        if (Array.isArray(block.agentIds)) extraLines.push(`Agents: ${block.agentIds.length}`);
                                                    } else if (type === 'stimuli') {
                                                        if (typeof block.stimuliCount === 'number') extraLines.push(`Stimuli: ${block.stimuliCount}`);
                                                        if (typeof block.questionCount === 'number') extraLines.push(`Questions/Trial: ${block.questionCount}`);
                                                    } else if (type === 'questionnaire') {
                                                        if (Array.isArray(block.questionTypes)) {
                                                            const typeFreq = block.questionTypes.reduce((acc,t)=>{acc[t]=(acc[t]||0)+1;return acc;},{});
                                                            extraLines.push('Q Types: '+ Object.entries(typeFreq).map(([k,v])=>`${k}:${v}`).join(' '));
                                                        }
                                                    }
                                                } else if (!isCustom && type === 'stimuli') {
                                                    if (block.stimuliSet?.name) extraLines.push(block.stimuliSet.name);
                                                }
                                                const blockTitle = isCustom ? block.title : block.title; // legacy rarely has title
                                                return (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{type}</Typography>
                                                                {blockTitle && blockTitle.trim() && (
                                                                    <Typography variant="subtitle2" color="text.secondary">{blockTitle}</Typography>
                                                                )}
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>Trials: {trialCount}</Typography>
                                {!isCustom && block.type === 'stimuli' && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {block.stimuliSet?.name || 'No stimuli set'}
                                  </Typography>
                                )}
                                                                {extraLines.map((ln,i)=>(
                                                                    <Typography key={i} variant="caption" color="text.secondary" sx={{ mt:0.5 }}>{ln}</Typography>
                                                                ))}
                            </Paper>
                        </Grid>
                        );
                    })}
                </Grid>
            </Box>
        );
    };

    // Add computed values for trials and blocks
    const blocks = Array.isArray(experiment.task.blocks) ? experiment.task.blocks : [];
    const isCustom = experiment.task?.type === 'custom_experiment';
    const { totalTrials, numBlocks } = isCustom ? deriveTotals(experiment._normalized) : {
        totalTrials: blocks.reduce((sum, block) => {
            if (block.type === 'stimuli') {
                return sum + (block.stimuliSet?.files?.length || 0);
            }
            return sum + (block.num_trials || 0);
        }, 0),
        numBlocks: blocks.length
    };

    const renderSummary = () => {
        const blocks = Array.isArray(experiment.task.blocks) ? experiment.task.blocks : [];
        const isCustom = experiment.task?.type === 'custom_experiment';
        const totalTrials = isCustom ? deriveTotals(experiment._normalized).totalTrials : blocks.reduce((sum, block) => {
            if (block.type === 'stimuli') {
                return sum + (block.stimuliSet?.files?.length || 0);
            }
            return sum + (block.num_trials || 0);
        }, 0);
        const totalAgents = experiment.agents.length;
        // const totalResponses = blocks.reduce((sum, block) => sum + (block.expected_responses || 0), 0); // unused

        return (
            <Box mt={2}>
                <Typography variant="h5" gutterBottom>Experiment Summary</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6">Total Agents</Typography>
                            <Typography variant="subtitle1">{totalAgents}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6">Total Trials</Typography>
                            <Typography variant="subtitle1">{totalTrials}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="h6">Number of Blocks</Typography>
                            <Typography variant="subtitle1">{blocks.length}</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box>
            <Tabs value={tabIndex} onChange={handleTabChange}>
                <Tab label="Details" />
                <Tab label={`Sessions (${sessions.length})`} />
            </Tabs>
            {tabIndex === 0 && (
                <Box mt={2}>
                    <Typography variant="h4" gutterBottom>
                        Experiment Details
                    </Typography>
                    {renderBlockSummary()}
                    {renderSummary()}
                    <Box mt={2}>
                        <Typography variant="h6"><strong>Experiment ID:</strong> {experiment.expt_id}</Typography>
                        <Typography variant="h6"><strong>Title:</strong> {experiment._normalized?.title || experiment.title}</Typography>
                        <Typography variant="h6"><strong>Date:</strong> {new Date(experiment.date).toLocaleString()}</Typography>
                        <Typography variant="h6"><strong>Task Type:</strong> {experiment.task.type}</Typography>
                        <Typography variant="h6"><strong>Number of Trials:</strong> {totalTrials}</Typography>
                        <Typography variant="h6"><strong>Number of Blocks:</strong> {numBlocks}</Typography>
                        
                    </Box>
                    <Typography variant="h4" gutterBottom>Agents</Typography>
                    <Box mt={2} sx={{ display: 'flex', overflowX: 'auto', gap: 2, py: 1 }}>
                        {experiment.agents.map((agent, index) => (
                            <Paper key={index} sx={{ flex: '0 0 33%', maxWidth: '33%', boxSizing: 'border-box', p: 2, borderRadius: 2 }} elevation={2}>
                                <Typography variant="h6"><strong>Agent ID:</strong> {agent.agent_id || agent.id}</Typography>
                                <Typography variant="subtitle1"><strong>Name:</strong> {agent.name}</Typography>
                                <Typography variant="subtitle2"><strong>Block:</strong> {agent.block}</Typography>
                                <Typography variant="subtitle2"><strong>Tools:</strong> {agent.tools?.join(', ')}</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2"><strong>Role:</strong> {agent.crew_settings?.role || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Backstory:</strong> {agent.crew_settings?.backstory || 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}><strong>Goal:</strong> {agent.crew_settings?.goal || 'N/A'}</Typography>
                            </Paper>
                        ))}
                    </Box>
                    {/* Block Configurations Section */}
                    <Box mt={4}>
                        <Typography variant="h4" gutterBottom>Block Configurations</Typography>
                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Block #</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Intro Text</TableCell>
                                        <TableCell>Outro Text</TableCell>
                                        <TableCell>Details</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                                        {blocks.map((block, index) => {
                                                                                const isCustom = experiment.task?.type === 'custom_experiment';
                                                                                const customBlock = isCustom ? (experiment._normalized?.blocks[index] || {}) : {};
                                                                                return (
                                                                                    <TableRow key={index}>
                                                                                        <TableCell>{index + 1}</TableCell>
                                                                                        <TableCell>{block.type}</TableCell>
                                                                                        <TableCell>{block.introduction_text || block.introText || 'N/A'}</TableCell>
                                                                                        <TableCell>{block.outroduction_text || block.outroText || 'N/A'}</TableCell>
                                                                                        <TableCell>
                                                                                            {/* Questionnaire */}
                                                                                            {!isCustom && block.type === 'questionnaire' && `Questions: ${block.num_trials}`}
                                                                                            {isCustom && block.type === 'questionnaire' && `Questions: ${(block.questions||[]).length} | Types: ${customBlock.questionTypes ? customBlock.questionTypes.slice(0,5).join(', ') + (customBlock.questionTypes.length>5?'…':'') : 'N/A'}`}
                                                                                            {/* Scale */}
                                                                                            {!isCustom && block.type === 'scale' && `Scale: "${block.scale?.name || 'N/A'}", Authors: ${block.scale?.authors?.join(', ') || 'N/A'}, Items: ${block.num_trials}`}
                                                                                            {isCustom && block.type === 'scale' && `Scale Items: ${(block.trials||[]).length}`}
                                                                                            {/* Conversation */}
                                                                                            {block.type === 'conversation' && `Trials: ${(block.trials||[]).length}`}
                                                                                            {/* Primed Conversation */}
                                                                                            {block.type === 'primed_conversation' && (
                                                                                                isCustom ? (
                                                                                                    block.conversationMode === 'single_for_block'
                                                                                                        ? `Mode: single | Trials: ${(block.conversation?.trials||[]).length} | Stimuli: ${(block.stimuli||[]).length} | Agents: ${(customBlock.agentIds||[]).length}`
                                                                                                        : `Mode: per_stimulus | Stimuli: ${(block.stimuli||[]).length} | Agents: ${(customBlock.agentIds||[]).length}`
                                                                                                ) : (
                                                                                                    block.conversationMode === 'single_for_block' ? `Trials: ${(block.conversation?.trials||[]).length}` : `Stimuli Conversations: ${(block.stimuli||[]).length}`
                                                                                                )
                                                                                            )}
                                                                                            {/* Unknown */}
                                                                                            {!['questionnaire','scale','conversation','primed_conversation','stimuli'].includes(block.type) && 'N/A'}
                                                                                            {/* Stimuli */}
                                                                                            {block.type === 'stimuli' && (
                                                                                                <div>
                                                                                                    {isCustom ? (
                                                                                                        <>
                                                                                                            <div><strong>Stimuli:</strong> {(block.stimuli||[]).length}</div>
                                                                                                            <div><strong>Questions/Trial:</strong> {(block.questions||[]).length}</div>
                                                                                                            {customBlock.stimuliSetNames && customBlock.stimuliSetNames.length>0 && (
                                                                                                                <div><strong>Stimuli Sets:</strong> {customBlock.stimuliSetNames.join(', ')}</div>
                                                                                                            )}
                                                                                                        </>
                                                                                                    ) : (
                                                                                                        <>
                                                                                                            <div><strong>Stimuli Set:</strong> {block.stimuliSet?.name || 'N/A'}</div>
                                                                                                            <div><strong>Trials:</strong> {block.stimuliSet?.files?.length || 0} stimuli</div>
                                                                                                            <div><strong>Questions per trial:</strong> {block.questions?.length || 0}</div>
                                                                                                            {block.settings && (
                                                                                                                <div>
                                                                                                                    <strong>Settings:</strong> {block.settings.randomizeOrder ? 'Random order' : 'Fixed order'}
                                                                                                                    {block.settings.minViewTime > 0 && `, Min view: ${block.settings.minViewTime}s`}
                                                                                                                    {block.settings.autoAdvance && `, Auto advance: ${block.settings.autoAdvanceTime}s`}
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                );
                                                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                 </Box>
             )}
            {tabIndex === 1 && (
                <Box mt={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h4" gutterBottom>
                            Sessions
                        </Typography>
                        <Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={(e) => handleMenuOpen(e, 'all')}
                                endIcon={<ArrowDropDownIcon />}
                            >
                                Download All
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl) && selectedRoom === 'all'}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={() => {handleDownload('all', 'raw'); handleMenuClose();}}>Raw Data</MenuItem>
                                <MenuItem onClick={() => {handleDownload('all', 'results'); handleMenuClose();}}>Results</MenuItem>
                                <MenuItem onClick={() => {handleDownload('all', 'metadata'); handleMenuClose();}}>Metadata</MenuItem>
                                <MenuItem onClick={() => {handleDownload('all', 'exptConfig'); handleMenuClose();}}>Expt Config</MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                     {sessions.map((session, index) => {
                         const timing = getSessionTiming(session);
                         return (
                        <Box key={index} mt={2} p={2} border={1} borderRadius={4} borderColor="grey.300">
                            <Box>
                                <Typography variant="h6"><strong>Room ID:</strong> {session.roomID}</Typography>
                                <Typography variant="h6"><strong>User ID:</strong> {session.userID}</Typography>
                                <Typography variant="h6"><strong>Status:</strong> {session.status}</Typography>
                                <Typography variant="h6"><strong>Date:</strong> {timing.startDate ? timing.startDate.toLocaleString() : 'N/A'}</Typography>
                                <Typography variant="h6"><strong>Duration:</strong> {timing.duration}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(e) => handleMenuOpen(e, session.roomID)}
                                    endIcon={<ArrowDropDownIcon />}
                                >
                                    Download
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl) && selectedRoom === session.roomID}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem onClick={() => {handleDownload(session.roomID, 'raw'); handleMenuClose();}}>Raw Data</MenuItem>
                                    <MenuItem onClick={() => {handleDownload(session.roomID, 'results'); handleMenuClose();}}>Results</MenuItem>
                                    <MenuItem onClick={() => {handleDownload(session.roomID, 'metadata'); handleMenuClose();}}>Metadata</MenuItem>
                                    <MenuItem onClick={() => {handleDownload(session.roomID, 'exptConfig'); handleMenuClose();}}>Expt Config</MenuItem>
                                </Menu>
                             </Box>
                        </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

export default ExperimentDetails;
